import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Fade } from "react-awesome-reveal";
import "swiper/css";

import SectionTitle from "../home/SectionTitle";
import SliderLine from "../home/SliderLine";

import prev from "../../img/home/prev.svg";
import next from "../../img/home/next.svg";

import tileStocks from "../../img/markets/tile-stocks.webp";
import tileIndices from "../../img/markets/tile-indices.webp";
import tileCommodities from "../../img/markets/tile-commodities.webp";
import tileCrypto from "../../img/markets/tile-crypto.webp";
import tileForex from "../../img/home/market-forex.webp";

const MARKETS = [
  {
    key: "stocks",
    img: tileStocks,
    nameKey: "markets.five.stocks.name",
    titleKey: "markets.five.stocks.title",
    features: ["markets.five.stocks.f1", "markets.five.stocks.f2", "markets.five.stocks.f3", "markets.five.stocks.f4"],
  },
  {
    key: "forex",
    img: tileForex,
    nameKey: "markets.five.forex.name",
    titleKey: "markets.five.forex.title",
    features: ["markets.five.forex.f1", "markets.five.forex.f2", "markets.five.forex.f3", "markets.five.forex.f4"],
  },
  {
    key: "indices",
    img: tileIndices,
    nameKey: "markets.five.indices.name",
    titleKey: "markets.five.indices.title",
    features: ["markets.five.indices.f1", "markets.five.indices.f2", "markets.five.indices.f3", "markets.five.indices.f4"],
  },
  {
    key: "commodities",
    img: tileCommodities,
    nameKey: "markets.five.commodities.name",
    titleKey: "markets.five.commodities.title",
    features: ["markets.five.commodities.f1", "markets.five.commodities.f2", "markets.five.commodities.f3", "markets.five.commodities.f4"],
  },
  {
    key: "crypto",
    img: tileCrypto,
    nameKey: "markets.five.crypto.name",
    titleKey: "markets.five.crypto.title",
    features: ["markets.five.crypto.f1", "markets.five.crypto.f2", "markets.five.crypto.f3", "markets.five.crypto.f4"],
  },
];

/* Ширины из макета (доли от 1220): тайл 271.2, активная карточка 407 */
const TILE_W = 22.23;
const CARD_W = 33.36;

function CardInfo({ market, t }) {
  return (
    <>
      <p className="font-['Inter'] font-bold text-[21px] text-white">{t(market.titleKey)}</p>
      <div className="mt-[15px] flex flex-col gap-[15px]">
        {market.features.map((f) => (
          <div key={f} className="flex items-center gap-[15px]">
            <div className="check-icon-blue">
              <Check className="w-[14px] h-[14px] text-[#4da6ff]" strokeWidth={2.5} />
            </div>
            <span className="font-['Inter'] font-normal text-[15px] lg:text-[16px] text-white">
              {t(f)}
            </span>
          </div>
        ))}
      </div>
    </>
  );
}

/* В мобильном слайдере Forex идёт первым, как в макете (1 / 5) */
const MOBILE_MARKETS = [...MARKETS.slice(1), MARKETS[0]];

function FiveMarkets() {
  const { t } = useTranslation();
  const swiperRef = useRef(null);
  const [current, setCurrent] = useState(0);
  const [active, setActive] = useState(1); // Forex активен, как в макете

  /* Окно из 4 слотов: активная карточка во втором слоте, где возможно */
  const windowStart = Math.min(Math.max(active - 1, 0), MARKETS.length - 4);
  const offsetPct = windowStart * TILE_W;

  return (
    <section className="mt">
      <div className="glcontainer">
        <div className="lg:pl-[22.2%]">
          <SectionTitle
            title1={t("markets.five.title1")}
            title2={t("markets.five.title2")}
            text={t("markets.five.subtitle")}
            align="left"
          />
        </div>

        {/* ── Desktop: лента тайлов с раскрытой карточкой ── */}
        <Fade triggerOnce direction="up" duration={700} className="hidden lg:block mt-[30px]">
        <div>
          <div className="relative overflow-hidden">
            <div
              className="flex items-center transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${offsetPct}%)` }}
            >
              {MARKETS.map((market, i) =>
                i === active ? (
                  <div
                    key={market.key}
                    className="shrink-0 bg-[#1c385f] rounded-[19px] px-[30px] py-[40px]"
                    style={{ width: `${CARD_W}%` }}
                  >
                    <CardInfo market={market} t={t} />
                  </div>
                ) : (
                  <button
                    key={market.key}
                    type="button"
                    onClick={() => setActive(i)}
                    className="relative shrink-0 h-[175px] overflow-hidden text-left group"
                    style={{ width: `${TILE_W}%` }}
                    aria-label={t(market.nameKey)}
                  >
                    <img
                      src={market.img}
                      alt=""
                      aria-hidden
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-[rgba(28,56,95,0)] to-[rgba(28,56,95,0.8)]" />
                    <span className="absolute left-[18px] bottom-[18px] font-['Inter'] font-bold text-[16px] text-white">
                      {t(market.nameKey)}
                    </span>
                  </button>
                )
              )}
            </div>
          </div>

          {/* Прогресс + стрелки */}
          <div className="mt-[53px] flex items-center justify-end gap-[19px]">
            <div className="w-[33.1%] h-[3px] rounded-[1px] bg-[#ebebeb] overflow-hidden">
              <div
                className="h-full rounded-[1px] bg-[#133261] transition-all duration-500"
                style={{ width: `${((active + 1) / MARKETS.length) * 100}%` }}
              />
            </div>
            <div className="flex items-center gap-[16px]">
              <button
                onClick={() => setActive((active - 1 + MARKETS.length) % MARKETS.length)}
                className="hover:scale-[1.05] transition-all"
                aria-label="Previous"
              >
                <img src={prev} alt="Previous" className="w-[42px] h-[42px]" />
              </button>
              <button
                onClick={() => setActive((active + 1) % MARKETS.length)}
                className="hover:scale-[1.05] transition-all"
                aria-label="Next"
              >
                <img src={next} alt="Next" className="w-[42px] h-[42px]" />
              </button>
            </div>
          </div>
        </div>
        </Fade>

        {/* ── Mobile / tablet: слайдер карточек ── */}
        <div className="lg:hidden mt-[25px]">
          <Swiper
            onSwiper={(swiper) => (swiperRef.current = swiper)}
            onSlideChange={(swiper) => setCurrent(swiper.realIndex)}
            spaceBetween={16}
            slidesPerView={1}
            grabCursor
            breakpoints={{ 640: { slidesPerView: 2, spaceBetween: 20 } }}
          >
            {MOBILE_MARKETS.map((market) => (
              <SwiperSlide key={market.key} className="!h-auto">
                <div className="h-full bg-[#1c385f] rounded-[19px] px-[30px] py-[40px]">
                  <CardInfo market={market} t={t} />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
          <SliderLine
            current={current}
            total={MARKETS.length}
            onPrev={() => swiperRef.current?.slidePrev()}
            onNext={() => swiperRef.current?.slideNext()}
          />
        </div>
      </div>
    </section>
  );
}

export default FiveMarkets;
